import React from 'react';
import moment from 'moment';
import { Box, Button, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AndroidIcon from '@mui/icons-material/Android';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple } from '@fortawesome/free-brands-svg-icons';

// From this many devices on, the list scrolls inside the box (about five rows
// visible) so the send buttons stay close to the form.
export const MANY_DEVICES_AT = 6;

const useStyles = makeStyles(() => ({
    label: {
        display: "block",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: 1.8,
        color: "#000"
    }
}))

const formatLastAccess = (iso) => {
    const date = moment(iso);
    const time = date.format('HH:mm');

    if (date.isSame(moment(), 'day')) return `hoje às ${time}`;
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return `ontem às ${time}`;

    return `${date.format('DD/MM/YYYY')} às ${time}`;
};

// The model name the device reported ("iPhone 14"), when there is one. Android
// model names are often codes ("SM-M305M"), so those are never shown.
const buildDeviceName = (device) => {
    if (device.os === 'ios') return device.modelName || 'Celular iOS';
    if (device.os === 'android') return 'Celular Android';

    return 'Celular';
};

function DeviceRow({ device, checked, isFresh, isRemoving, onToggle, onRemove }) {
    const isIos = device.os === 'ios';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                p: '8px 8px 8px 4px',
                borderBottom: '1px solid rgba(0,0,0,.12)',
                backgroundColor: isFresh ? 'rgba(60,213,104,.1)' : 'transparent',
                transition: 'background-color 1.2s'
            }}
        >
            <Checkbox checked={checked} onChange={onToggle} />

            <Box sx={{ width: 28, flex: 'none', display: 'flex', justifyContent: 'center' }}>
                {isIos
                    ? <FontAwesomeIcon icon={faApple} style={{ fontSize: 24, color: '#1c93c3' }} />
                    : <AndroidIcon sx={{ fontSize: 24, color: '#2cc69d' }} />
                }
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onToggle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1">{buildDeviceName(device)}</Typography>
                    {isFresh &&
                        <Chip
                            label="conectado agora"
                            sx={{ height: 22, borderRadius: '16px', backgroundColor: '#a5d6a7', color: '#1b5e20', fontWeight: 700, fontSize: 12 }}
                        />
                    }
                </Box>
                <Typography variant="caption" component="div" sx={{ color: 'rgba(0,0,0,.6)' }}>
                    Último acesso: {formatLastAccess(device.lastAccessAt)} · Pareado em {moment(device.pairedAt).format('DD/MM/YYYY')}
                </Typography>
            </Box>

            <Tooltip title="Remover">
                <span>
                    <IconButton onClick={onRemove} disabled={isRemoving}>
                        <DeleteIcon sx={{ color: isRemoving ? undefined : 'rgba(0,0,0,.54)' }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    )
}

/**
 * "Enviar para" of the push screen while the app is on trial: the devices with the
 * Visualizador connected to the store, which are the only ones a trial push can
 * reach. The devices come sorted by last access, most recent first, so a device
 * that was just paired is already on top.
 */
export default function PreviewerDevicesField({ devices, selectedIds, freshDeviceId, removingIds, onToggle, onRemove, onPair }) {
    const classes = useStyles();

    const isMany = devices.length >= MANY_DEVICES_AT;
    const selectedCount = devices.filter((d) => selectedIds.has(d.id)).length;

    const caption = devices.length === 0
        ? 'O celular aparece aqui assim que for conectado.'
        : isMany
            ? `${selectedCount} de ${devices.length} celulares selecionados. A lista está ordenada pelo último acesso.`
            : 'Celulares com o Visualizador Mobilizei conectado à sua loja.';

    const rows = devices.map((device) => (
        <DeviceRow
            key={device.id}
            device={device}
            checked={selectedIds.has(device.id)}
            isFresh={device.id === freshDeviceId}
            isRemoving={removingIds.has(device.id)}
            onToggle={() => onToggle(device.id)}
            onRemove={() => onRemove(device.id)}
        />
    ));

    return (
        <Box>
            <label className={classes.label}>Enviar para</label>

            <Box sx={{ border: '1px solid rgba(0,0,0,.23)', borderRadius: '4px', overflow: 'hidden' }}>
                {devices.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: '6px',
                            p: '28px 20px 24px'
                        }}
                    >
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <SmartphoneOutlinedIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>Nenhum celular conectado</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,.6)', maxWidth: 340 }}>
                            Conecte o seu celular ao Visualizador Mobilizei para receber a notificação de teste.
                            Você pode continuar escrevendo a mensagem enquanto isso.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={onPair}
                            startIcon={<QrCodeScannerIcon sx={{ fontSize: 18 }} />}
                            sx={{ mt: 1 }}
                        >
                            conectar celular
                        </Button>
                    </Box>
                ) : (
                    <>
                        {isMany
                            ? <Box sx={{ maxHeight: 310, overflowY: 'auto' }}>{rows}</Box>
                            : rows
                        }
                        <Button
                            fullWidth
                            onClick={onPair}
                            startIcon={<QrCodeScannerIcon sx={{ fontSize: 20 }} />}
                            sx={{
                                justifyContent: 'flex-start',
                                p: '14px 16px',
                                borderRadius: 0,
                                fontWeight: 700,
                                fontSize: 14,
                                textTransform: 'none',
                                color: '#2BAA4F',
                                '&:hover': { backgroundColor: 'rgba(60,213,104,.06)' }
                            }}
                        >
                            Parear outro celular
                        </Button>
                    </>
                )}
            </Box>

            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>{caption}</Typography>
        </Box>
    )
}
